import { useState } from 'react'
import ClearIcon from '@mui/icons-material/Clear'
import { Pencil, Trash2, Check, X } from 'lucide-react'
import CustomButton from '../../../../ui/CustomButton'
import { Modal } from '../../../../ui/Modal'

const CardItem = ({ card, onCheckCanDelete, onDelete, onDeleteConflict, onEdit }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editName, setEditName] = useState(card.name || '')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(false)

  const handleSaveEdit = async () => {
    if (editName.trim()) {
      await onEdit(card.id, editName.trim())
      setIsEditing(false)
    }
  }

  const handleCancelEdit = () => {
    setEditName(card.name || '')
    setIsEditing(false)
  }

  const handleDeleteClick = async () => {
    if (!onCheckCanDelete) {
      setShowDeleteConfirm(true)
      return
    }

    setChecking(true)

    try {
      const result = await onCheckCanDelete(card.id)

      if (result?.canDelete) {
        setShowDeleteConfirm(true)
      } else if (onDeleteConflict) {
        onDeleteConflict(card, result || {})
      }
    } catch (error) {
      console.error('Error al verificar:', error)
    } finally {
      setChecking(false)
    }
  }

  const handleConfirmDelete = async () => {
    setLoading(true)

    try {
      await onDelete(card.id)
      setShowDeleteConfirm(false)
    } catch (error) {
      console.error('Error al eliminar la tarjeta:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false)
  }

  const getCardTypeIcon = cardType => {
    const icons = {
      visa: '💳',
      master: '💳',
      amex: '💳',
      naranja: '💳',
      cabal: '💳',
      diners: '💳'
    }

    return icons[cardType.toLowerCase()] || '💳'
  }

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString('es-CL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  return (
    <div className='bg-white border border-gray-200 rounded-lg p-6 shadow-sm'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-4'>
          <div className='text-4xl'>{getCardTypeIcon(card.cardType)}</div>
          <div>
            <div className='flex items-center gap-2 mb-1'>
              {isEditing ? (
                <>
                  <input
                    type='text'
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    className='border border-gray-300 rounded px-2 py-1 text-sm font-semibold text-lg'
                    placeholder='Nombre de la tarjeta'
                    autoFocus
                  />
                  <div
                    className='bg-green-600 rounded-full p-1.5 cursor-pointer hover:bg-green-700 flex items-center justify-center'
                    onClick={handleSaveEdit}
                  >
                    <Check className='w-4 h-4 text-white' />
                  </div>
                  <div
                    className='bg-gray-400 rounded-full p-1.5 cursor-pointer hover:bg-gray-500 flex items-center justify-center'
                    onClick={handleCancelEdit}
                  >
                    <X className='w-4 h-4 text-white' />
                  </div>
                </>
              ) : (
                <h3 className='font-semibold text-lg'>
                  {card.name || `${card.cardType.toUpperCase()} **** ${card.lastFourDigits}`}
                </h3>
              )}
            </div>
            <p className='text-sm text-gray-600'>
              {card.cardType.toUpperCase()} **** {card.lastFourDigits}
            </p>
            <p className='text-xs text-gray-500'>Guardada el {formatDate(card.createdAt)}</p>
          </div>
        </div>

        {!isEditing && (
          <div className='flex items-center gap-3'>
            <div
              className='bg-white rounded-full p-2 border border-gray-200 cursor-pointer hover:bg-gray-100 flex items-center justify-center'
              onClick={() => setIsEditing(true)}
            >
              <Pencil className='w-5 h-5 text-gray-600' />
            </div>
            <div
              className={`rounded-full p-2 flex items-center justify-center ${
                checking ? 'bg-red-400 cursor-wait' : 'bg-red-600 cursor-pointer hover:bg-red-700'
              }`}
              onClick={checking ? undefined : handleDeleteClick}
            >
              <Trash2 className='w-5 h-5 text-white' />
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={showDeleteConfirm} onClose={handleCancelDelete}>
        <div className='flex max-h-[80vh] w-[min(36rem,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl bg-white shadow-2xl'>
          <div className='flex items-center justify-end bg-primary px-4 py-2 text-white sm:px-8'>
            <button
              type='button'
              onClick={handleCancelDelete}
              className='rounded-full p-2 text-white transition-colors hover:bg-white/15 disabled:cursor-not-allowed disabled:opacity-50'
              disabled={loading || checking}
              aria-label='Cerrar modal'
            >
              <ClearIcon style={{ fontSize: 20 }} />
            </button>
          </div>

          <div className='flex-1 overflow-y-auto px-6 py-8 sm:px-8'>
            <div className='mb-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-5 shadow-sm'>
              <h3 className='text-2xl font-sans font-bold text-slate-800'>Eliminar tarjeta</h3>
              <p className='mt-3 text-sm font-sans leading-6 text-slate-700'>
                Confirma si realmente quieres eliminar este medio de pago.
              </p>
            </div>

            <div className='space-y-4'>
              <div className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
                <p className='text-[11px] font-sans font-semibold uppercase tracking-[0.18em] text-slate-500'>
                  Tarjeta
                </p>
                <p className='mt-2 text-sm font-sans font-semibold text-slate-800'>
                  {card.cardType.toUpperCase()} **** {card.lastFourDigits}
                </p>
              </div>

              <div className='rounded-2xl border border-gray-200 bg-gray-50 p-4'>
                <p className='text-sm font-sans font-medium'>
                  Esta accion elimina la tarjeta guardada de tu organizacion.
                </p>
              </div>
            </div>
          </div>

          <div className='flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:px-8'>
            <CustomButton
              title='Cancelar'
              color='primary'
              className='rounded-full px-4 py-2'
              onClick={handleCancelDelete}
              disabled={loading || checking}
            />
            <CustomButton
              title={loading ? 'Eliminando...' : checking ? 'Verificando...' : 'Eliminar'}
              color='accent3'
              className='rounded-full px-4 py-2'
              onClick={handleConfirmDelete}
              disabled={loading || checking}
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default CardItem
